import * as ko from 'knockout';
import { error } from '../../../debug';
import { DONE } from '../../../helper';
import { IUpdateItemStateInput } from '../../../its-itembank-api.g';
import * as RICHTEXTHTML from '../../../richtext/html';
import { ServerConnection } from '../../../ui/RestAPI';
import { INIT, ItemDataModel, translate } from '../ItemDataModel';
import { ItemModel } from '../ItemModel';

function getData(item: INIT) {
    if (item.data.__typename === 'KosovoTextEntry_Manual_QueryItemSessionData') {
        return item.data;
    }
    throw new Error();
}

type DATA = ReturnType<typeof getData>;
type GAP = DATA['temGaps'][0];

export class TextEntryManualData_Gap {
    public readonly gap: GAP;
    constructor(readonly model: TextEntryManualData, readonly gapId: string) {
        this.gap = this.model._data.temGaps.find(val => val.id === gapId);
        this.isCorrect = this.gap.isCorrect;
        this.validationMessage = translate(this.gap.validationMessage, {});
        const pattern = this.gap.pattern && ('^' + this.gap.pattern + '$');
        try {
            if (pattern) {
                this.pattern = new RegExp(this.gap.pattern);
            }
        } catch (e) {
            error(`RegEx Pattern for gap ${gapId} is not valid: ${this.gap.pattern}`);
        }
        let givenAnswer = ko.observable<string>();
        if (this.pattern) {
            givenAnswer = givenAnswer.extend(<any>{
                pattern: {
                    message: this.validationMessage,
                    params: pattern,
                }
            });
        }
        givenAnswer(this.gap.givenAnswer);
        this.givenAnswer = givenAnswer;
    }
    public readonly validationMessage: string;
    public readonly pattern: RegExp;
    public readonly givenAnswer = ko.observable<string>();
    public readonly isCorrect: boolean;

    public readonly IsInteractionComplete = ko.pureComputed(() => {
        const given = this.givenAnswer();
        if (!given) {
            return false;
        }
        if (this.pattern) {
            if (!this.pattern.test(given)) {
                return false;
            }
        }
        return true;
    });
}

export class TextEntryManualData_Text {
    constructor(readonly model: TextEntryManualData, readonly fragmentText: string) {
        this.text = this.fragmentText;
        this.html = RICHTEXTHTML.process({
            inline: true,
            html: this.fragmentText,
            attachments: this.model.meta.attachments,
        });
    }
    public readonly text: string;
    public readonly html: string;
}


export class TextEntryManualData extends ItemDataModel {
    constructor(readonly meta: ItemModel, readonly _data: DATA) {
        super();
        this.headerText = translate(_data.header, {});
        this.questionHTML = RICHTEXTHTML.process({
            html: translate(_data.question, {}),
            attachments: this.meta.attachments,
        });

        const templateText = translate(_data.temText, {});
        const regEx = /\$\(gap:([^)]+)\)/g;
        const fragments = templateText.split(regEx);
        this.fragments = fragments.map((text, i) => {
            if (i % 2 === 0) {
                return new TextEntryManualData_Text(this, fragments[i]);
            } else {
                return new TextEntryManualData_Gap(this, fragments[i]);
            }
        });
        this.gaps = <TextEntryManualData_Gap[]>this.fragments.filter(x => x instanceof TextEntryManualData_Gap);

        const imgAtt = meta.getAttachment(_data.temImage?.id);
        this.imgUrl = ServerConnection.getDataUrl(imgAtt?.hrefResolved);
        this.imgAlt = imgAtt?.altText?.value ?? '';
        this.hasImage = !!this.imgUrl;
        this.fromJS(_data);
    }
    public fromJS(data: DATA) {
    }
    public readonly hasImage: boolean;
    public readonly imgUrl: string;
    public readonly imgAlt: string;
    public readonly headerText: string;
    public readonly questionHTML: string;
    public readonly fragments: Array<TextEntryManualData_Gap | TextEntryManualData_Text>;
    public readonly gaps: Array<TextEntryManualData_Gap>;

    public setAnwer({ gapId, value }: { gapId: string, value: string }) {
        const gap = this.gaps.find(x => x.gapId === gapId);
        if (!gap) {
            throw new Error();
        }
        if (gap) {
            gap.givenAnswer(value);
        }
    }


    public IsInteractionStarted() {
        return this.gaps.some(x => !!x.givenAnswer());
    }
    public IsInteractionComplete() {
        return this.gaps.every(x => x.IsInteractionComplete());
    }
    public async reset() {
        for (const gap of this.gaps) {
            gap.givenAnswer('');
        }
        return DONE;
    }
    public getItemState() {
        const retVal: IUpdateItemStateInput = {
            itemId: this.meta.itemId,
            TextEntryManual: {
                gaps: this.gaps.map(x => ({
                    gapId: x.gapId,
                    text: x.givenAnswer(),
                }))
            }
        };
        return retVal;
    }

}